/**
 * V2 Import hub — /import, "Bring your places to Yoink" (default-app
 * wave 2, founder call 2026-08-04: every importer gets ONE front door,
 * reached from your own journal, instead of accreting inside Lists).
 *
 * A directory, not a tool: each card explains one way in and hands off
 * to the feature that does the work (the Takeout lists importer, the
 * per-collection stamping run, later the reviews importer). New
 * importers add a card here and nothing else moves.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { fetchAllCategories, categorySlug } from '../../core/categories.js';
import { categoryIcon } from '../../ui/icons.js';

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  let catalog = null; // null until loaded — the collections card waits on it

  function followedSets() {
    if (!catalog) return [];
    const followed = new Set(store.get().followedCategoryIds || []);
    return catalog.filter((c) => c.is_set && followed.has(c.id));
  }

  function collectionsCardHtml() {
    const user = store.get().user;
    if (!user) {
      return '<p class="log-hint">Sign in (top right) and follow a collection to stamp your visits.</p>';
    }
    const sets = followedSets();
    if (catalog && !sets.length) {
      return `<p class="log-hint">You’re not following any collections yet —
        <a href="${basePath}/categories" data-hub-browse>browse the catalog</a> and follow one first.</p>`;
    }
    return `
      <div class="hub-set-chips">
        ${sets.map((s) => `
        <button type="button" class="v2-chip" data-hub-stamp="${escapeHtml(categorySlug(s))}">
          ${categoryIcon(s, 16)}${escapeHtml(s.name)}</button>`).join('')}
      </div>`;
  }

  /**
   * Unfinished imports are findable HERE, not only inside the importer
   * that made them (2026-08-04: "skip for now puts them in a waiting
   * list — but where is that list?"). Reads the same per-device session
   * keys the two importers write.
   */
  function parkedHtml() {
    const read = (key) => {
      try {
        return JSON.parse(localStorage.getItem(key) || 'null');
      } catch {
        return null;
      }
    };
    const rows = [];
    const maps = read('yoink_import_session');
    if (maps?.allRows?.length) {
      const left = maps.allRows.length - (Number(maps.matchedUpTo) || 0);
      rows.push(`<button type="button" class="v2-btn-leather" data-hub-google>
        Finish “${escapeHtml(maps.fileName || 'your Google Maps list')}”${left ? ` — ${left} still to match` : ''}</button>`);
    }
    const reviews = read('yoink_reviews_session');
    const waiting = (reviews?.queue?.length || 0) + (reviews?.skipped?.length || 0);
    if (waiting) {
      rows.push(`<button type="button" class="v2-btn-leather" data-hub-reviews>
        Finish your reviews — ${waiting} waiting${reviews.skipped?.length
          ? ` (${reviews.skipped.length} set aside)` : ''}</button>`);
    }
    if (!rows.length) return '';
    return `
        <section class="hub-card hub-card-parked" aria-label="Unfinished imports">
          <h3>Waiting for you</h3>
          <p>Picked up right where you left off — nothing is looked up twice.</p>
          ${rows.join('')}
        </section>`;
  }

  function render() {
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <section class="v2-import" aria-label="Bring your places to Yoink">
        <h2 class="v2-page-title">Bring your places to Yoink</h2>
        <p>Years of trips live in other apps and in your memory. Each of these
          brings them into your journal — pick whichever fits.</p>

        <section class="hub-card" aria-label="Import from Google Maps">
          <h3>Your Google Maps lists</h3>
          <p>Want to go, Favorites, or any custom list — imported into a Yoink
            list, notes and all.</p>
          <button type="button" class="v2-btn-leather" data-hub-google>Import a Google Maps list</button>
        </section>

        ${parkedHtml()}

        <section class="hub-card" aria-label="Import your Google reviews">
          <h3>Your Google reviews</h3>
          <p>Every review you’ve written is a visit with the words already
            written. Bring the keepers into your journal — text and date come
            along; you add a photo and a category.</p>
          <button type="button" class="v2-btn-leather" data-hub-reviews>Import your Google reviews</button>
        </section>

        <section class="hub-card" aria-label="Mark your visits">
          <h3>Mark your visits</h3>
          <p>Been collecting longer than you’ve had Yoink? Tick the places
            you’ve already been in a collection and stamp them one at a time —
            a photo and a rough date each, straight into your journal.</p>
          ${collectionsCardHtml()}
        </section>

        <p class="log-hint hub-foot">Setting up a new device too?
          <a href="${basePath}/phone" data-hub-phone>Put Yoink on your phone</a>.</p>
      </section>`;
  }

  function onClick(event) {
    if (event.target.closest('[data-hub-google]')) {
      router.navigate('lists-import');
      return;
    }
    if (event.target.closest('[data-hub-reviews]')) {
      router.navigate('import-reviews');
      return;
    }
    const stampChip = event.target.closest('[data-hub-stamp]');
    if (stampChip) {
      router.navigate('stamp', { slug: stampChip.dataset.hubStamp });
      return;
    }
    const local = event.target.closest('[data-hub-browse], [data-hub-phone], a[data-route]');
    if (local) {
      event.preventDefault();
      if (local.dataset.hubBrowse !== undefined) router.navigate('categories');
      else if (local.dataset.hubPhone !== undefined) router.navigate('phone');
      else router.navigate(local.dataset.route);
    }
  }

  el.addEventListener('click', onClick);
  render();
  fetchAllCategories()
    .then((cats) => {
      if (destroyed) return;
      catalog = cats;
      render();
    })
    .catch(() => { /* the collections card just keeps its loading-less state */ });
  // Follows load after auth settles — re-render when they land
  const unsubscribe = store.subscribe(() => {
    if (!destroyed && catalog) render();
  });

  return () => {
    destroyed = true;
    unsubscribe();
    el.removeEventListener('click', onClick);
  };
}
